import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { UserCog } from 'lucide-react'
import { columns } from './columns'
import { DataTable } from './data-table'
import { useProviderClinicUsers } from '@/hooks/useProviderClinicUsers'
import { useClinics } from '@/hooks/useClinics'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/PageHeader'
import { NoPermissionView } from '@/components/NoPermissionView'
import { isForbiddenError } from '@/utils/apiError'

const HEADER_PROPS = {
  breadcrumb: '工具箱',
  title: '管理所有診所人員',
  description: '集中檢視並管理 Provider 下所有診所的使用者',
  icon: <UserCog className="h-8 w-8" />,
  level: 'section' as const,
}

export default function ProviderClinicUsersPage() {
  const [account, setAccount] = useState('')
  const [selectedClinicId, setSelectedClinicId] = useState<number | undefined>(undefined)

  // 取得所有診所（不分頁）
  const { clinics } = useClinics({ page: 1, pageSize: 10000 })

  // 取得所有診所人員（不分頁）
  const { clinicUsers, isLoading, isError, mutate } = useProviderClinicUsers({
    page: 1,
    pageSize: 10000,
    account,
    clinicId: selectedClinicId,
  })

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader {...HEADER_PROPS} />
        {isForbiddenError(isError) ? (
          <NoPermissionView />
        ) : (
          <div className="text-center text-destructive">載入診所使用者列表失敗。請稍後再試。</div>
        )}
      </div>
    )
  }

  // 只在首次載入（沒有任何資料）時顯示 Skeleton
  // 避免搜尋或過濾時重置 DataTable 導致輸入框被清空
  const isInitialLoading = isLoading && !clinicUsers

  if (isInitialLoading) {
    return (
      <div className="space-y-6">
        <PageHeader {...HEADER_PROPS} />
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader {...HEADER_PROPS} />

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={clinicUsers || []}
            onSearch={setAccount}
            clinics={clinics}
            selectedClinicId={selectedClinicId}
            onClinicChange={setSelectedClinicId}
            meta={{
              reloadData: () => mutate(),
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}
